import React, { useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import clsx from 'clsx';
import Agenda from '../../assets/Agenda';
import { baseMoment } from '../../helpers/dates';
import { DIVIDER_HEIGHT, BLOCK_SPACING_X } from '../Schedule/Block';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: -theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      width: '100%',
      margin: theme.spacing(1, 0, 2, 0),
      justifyContent: 'center',
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    width: '100%',
    paddingLeft: theme.spacing(10.25),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  divider: {
    width: `calc(74% - ${2 * BLOCK_SPACING_X}px)`,
    height: DIVIDER_HEIGHT,
    backgroundColor: theme.palette.darkGrey.main,
    margin: theme.spacing(1.5, 0),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  icon: {
    height: 20,
    width: 20,
    color: 'unset',
    marginLeft: theme.spacing(1),
  },
  dateTitle: {
    color: 'unset',
    fontFamily: 'Fancy Cut Pro',
    fontWeight: 400,
    fontSize: 20,
    lineHeight: '100%',
    textAlign: 'center',
    letterSpacing: 0,
    marginLeft: theme.spacing(1),
  },
  dateButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    gap: theme.spacing(6),
    paddingLeft: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      gap: theme.spacing(2),
      overflowX: 'auto',
      width: '100%',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      scrollbarWidth: 'none',
    },
  },
  dateButton: {
    display: 'block',
    padding: 'unset',
    minWidth: '16px',
    [theme.breakpoints.down('sm')]: {
      minWidth: 40,
      padding: theme.spacing(0.5),
    },
  },
  day: {
    fontFamily: 'Fancy Cut Pro',
    fontWeight: 800,
    fontSize: 20,
    lineHeight: '100%',
    textAlign: 'center',
    color: 'unset',
    fontStyle: 'bold',
    letterSpacing: 0,
  },
  dayName: {
    fontFamily: 'Fancy Cut Pro',
    fontWeight: 400,
    fontSize: 10,
    lineHeight: '100%',
    textAlign: 'center',
    color: 'unset',
    letterSpacing: 0,
  },
  activeButton: {
    '& p': {
      color: theme.palette.primary.main,
      fontWeight: 800,
      fontStyle: 'bold',
    },
  },
}));

const DayPicker = ({ onChange, options, day, allowAll }) => {
  const classes = useStyles();

  useEffect(() => {
    // eslint-disable-next-line curly
    if (!day && options.length > 0) {
      // set default date
      if (allowAll && typeof day === 'undefined') onChange('');
      else if (!allowAll) {
        const now = baseMoment();
        let defaultDate;
        let currentDay;
        let previousDay;
        let firstNextDay;
        options.forEach((date) => {
          if (baseMoment(date).isSame(now, 'day')) currentDay = date;
          else if (!previousDay) previousDay = date;
          else if (baseMoment(date).isAfter(previousDay)
            && baseMoment(date).isBefore(now)) previousDay = date;
          else if (!firstNextDay) firstNextDay = date;
          else if (baseMoment(date).isBefore(firstNextDay)
            && baseMoment(date).isAfter(now)) firstNextDay = date;
        });
        if (currentDay) defaultDate = currentDay;
        else if (previousDay) defaultDate = previousDay;
        else defaultDate = firstNextDay;

        onChange(defaultDate);
      }
    }
  }, [day, allowAll, onChange, options]);

  if (options.length === 0) return null;
  const month = baseMoment(options[0]).format('MMMM, YYYY');

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Typography className={classes.dateTitle}>
          {month}
          <Agenda className={classes.icon} />
        </Typography>
        <Divider className={classes.divider} />
        <div className={classes.dateButtonsContainer}>
          {options.sort().map((date) => (
            <Button
              variant="text"
              onClick={() => {
                onChange(date);
              }}
              className={
                clsx(classes.dateButton, date === day && classes.activeButton)
              }
            >
              <Typography className={classes.day}>
                {baseMoment(date).format('DD')}
              </Typography>
              <Typography className={classes.dayName}>
                {baseMoment(date).format('ddd')}
              </Typography>
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

DayPicker.propTypes = {
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  day: PropTypes.string,
  allowAll: PropTypes.bool,
};

DayPicker.defaultProps = {
  day: '',
  allowAll: false,
};

export default DayPicker;
